import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import { Capacitor } from '@capacitor/core';
import { ConsentAppVendorSlug } from 'src/app/legal/consent/consent-vendors.config';
import { ConsentService } from 'src/app/legal/consent/consent.service';
import { Events } from 'src/app/@core/event.service';
import { Injectable } from '@angular/core';
import { MatomoService } from 'src/app/matomo/matomo.service';
import { ProtoReturnValue } from 'src/app/@core/types';
import { StorageService } from 'src/app/@core/storage.service';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';
import type { PageTrackEvent } from 'src/app/@shared/page-track.decorator';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  public isTrackingEnabled: boolean = false;

  private isInitialized: boolean = false;
  private isPublicTrackingInitialized: boolean = false;

  constructor(private consentService: ConsentService,
    private events: Events,
    private storage: StorageService,
    private matomoService: MatomoService) {
    // Public web analytics is handled by Matomo only, after explicit consent.
    if (isPublicWebAppBuild() && !Capacitor.isNativePlatform()) {
      this.events.subscribe('view:enter', (data: ProtoReturnValue) => {
        this.matomoService.trackPageView((data as PageTrackEvent).pageTitle);
      });

      return;
    }

    let appStarts = this.storage.get('appStarts', 0) as number;

    if (appStarts === 0) {
      this.storage.set('firstVisit', new Date().toISOString());
    }

    this.storage.set('lastVisit', new Date().toISOString());
    this.storage.set('appStarts', appStarts += 1);
  }

  public init(): void {
    if (isPublicWebAppBuild() && !Capacitor.isNativePlatform()) {
      if (this.isPublicTrackingInitialized) {
        return;
      }

      this.isPublicTrackingInitialized = true;
      this.matomoService.init();

      return;
    }

    this.consentService.getAcceptedVendorSlugs().subscribe(async (slugs: ConsentAppVendorSlug[]) => {
      if (!slugs) {
        return;
      }

      // !! based on CONSENT_APP_VENDOR_MAPPING config, we need to update `slugs.include(...)` part !!
      this.isTrackingEnabled = slugs.includes('googleAnalyticsForFirebase') && this.storage.get('appTrackingTransparencyAllowed');

      if (this.isTrackingEnabled) {
        await this.enable();
      } else {
        await this.disable();
      }
    });
  }

  public async enable(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Firebase analytics disabled for web');

      return Promise.resolve();
    }

    await FirebaseAnalytics.setEnabled({
      enabled: true,
    });

    await new Promise((resolve) => {
      console.warn('uncomment FirebaseAnalytics when plugin is ready for cap6');

      requestAnimationFrame(resolve);
    });

    return this.onTrackingEnabled();
  }

  public disable(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Firebase analytics disabled for web');

      return Promise.resolve();
    }

    return FirebaseAnalytics.setEnabled({
      enabled: false,
    });
  }

  public async onTrackingEnabled(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    const appStarts = this.storage.get('appStarts', 1) as number;

    // already in constructor intitialized to 1 at first App Start
    if (appStarts === 1) {
      await this.track('firstVisit');
    }

    await this.track('appStart', {
      appStarts,
    });

    this.bindEvents();
  }

  private bindEvents(): void {
    this.events.subscribe('view:enter', (data: ProtoReturnValue) => {
      this.track('page_view', {
        pageTitle: (data as PageTrackEvent).pageName,
      }).catch();
    });
  }

  private track(eventName: string, eventProperties?: ProtoReturnValue): Promise<void> {
    console.info('TrackingService.track', eventName, eventProperties);

    if (!this.isTrackingEnabled) {
      console.warn('Tracking is disabled');

      return Promise.resolve();
    }

    console.info('Tracking', eventName, eventProperties);

    if (!Capacitor.isNativePlatform()) {
      console.warn('Firebase analytics disabled for web');

      return Promise.resolve();
    }

    return FirebaseAnalytics.logEvent({
      name: eventName,
      params: eventProperties || {},
    });
  }
}
